class TmpNativeInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.loading = false;
    this.observer = undefined;
    this.sentinel = undefined;
  }

  connect() {
    if (!this.container || !this.sectionId) return;
    this.disconnect();
    this.pagination = this.container.querySelector('.pagination-wrapper');
    this.nextUrl = this.pagination?.dataset.nextUrl;
    if (!this.nextUrl) return;

    this.pagination.hidden = true;
    this.ensureSentinel();
    this.observe();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
  }

  ensureSentinel() {
    this.sentinel = this.container.querySelector('[data-native-infinite-scroll-sentinel]');
    if (this.sentinel) return;

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'tmp-bucketed-load-more';
    this.sentinel.dataset.nativeInfiniteScrollSentinel = 'true';
    this.sentinel.setAttribute('aria-live', 'polite');
    this.sentinel.textContent = 'Loading more products...';

    const collection = this.container.querySelector('.collection');
    if (collection) collection.appendChild(this.sentinel);
  }

  observe() {
    if (!this.sentinel) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '700px 0px 700px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  buildFetchUrl() {
    const url = new URL(this.nextUrl, window.location.origin);
    url.searchParams.set('section_id', this.sectionId);
    return url;
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    this.loading = true;
    this.disconnect();
    this.container.dataset.nativeInfiniteScrollState = 'loading';

    try {
      const response = await fetch(this.buildFetchUrl().toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Native infinite scroll request failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextContainer = doc.querySelector('#ProductGridContainer');
      const nextGrid = nextContainer?.querySelector('#product-grid');
      const currentGrid = this.container.querySelector('#product-grid');
      if (!nextContainer || !nextGrid || !currentGrid) throw new Error('Native infinite scroll response missing grid');

      nextGrid.querySelectorAll('.grid__item').forEach((item) => {
        currentGrid.appendChild(item.cloneNode(true));
      });

      const nextPagination = nextContainer.querySelector('.pagination-wrapper');
      this.nextUrl = nextPagination?.dataset.nextUrl || '';
      if (this.pagination) this.pagination.dataset.nextUrl = this.nextUrl;

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }
    } catch (error) {
      console.warn(error);
      if (this.pagination) this.pagination.hidden = false;
      this.nextUrl = '';
    } finally {
      this.loading = false;
      this.container.dataset.nativeInfiniteScrollState = this.nextUrl ? 'ready' : 'complete';

      if (this.nextUrl) {
        this.observe();
      } else if (this.sentinel) {
        this.sentinel.hidden = true;
      }
    }
  }
}

const initTmpNativeInfiniteScroll = () => {
  document.querySelectorAll('[data-native-infinite-scroll="true"]').forEach((container) => {
    if (container.tmpNativeInfiniteScroll) container.tmpNativeInfiniteScroll.disconnect();
    container.tmpNativeInfiniteScroll = new TmpNativeInfiniteScroll(container);
    container.tmpNativeInfiniteScroll.connect();
  });
};

document.addEventListener('DOMContentLoaded', initTmpNativeInfiniteScroll);
document.addEventListener('tmp:facets-rendered', initTmpNativeInfiniteScroll);
